import {useRequestOptions} from "~/composables/requestOptions"
import type {NuxtError} from "#app/composables/error"
import {defineStore} from "pinia"
import { useCheckoutStore } from "./checkout"

export const useAccountStore = defineStore('account', {
  state: (): Account => <Account>({
    phone: {
      phone: null,
      iso: null,
      calling_code: null,
      country: null
    },
    user: {
      name: null,
      id: null,
      profile_picture: null,
      email: null,
      language: null
    },
    authentication: {
      pin: null,
      fallback: false,
      expiration: null
    },
    registered: false,
    isSubmitting: false,
    isLoggedIn: false,
  }),

  actions: {
    verifyPin: async function (): Promise<{ user: User, registered: boolean } | NuxtError> {
      const requestOptions = useRequestOptions()
      const checkout = useCheckoutStore();
      const {data, error}: any = await useFetch(
        '/account/pin_validation',
        {
          ...requestOptions,
          body: {
            payment_intent: checkout.$state.pi.id,
            mobile_phone: this.phone.iso,
            pin: this.authentication.pin
          }
        }
      )

      if (error.value) {

        if (error.value.statusCode === 400) {
          let statusMessage: string = 'modal.pin.wrong_code'
          let statusCode: number = 400

          if (error.value.data.error.code === 'operation_not_found') {
            statusCode = 500
          }

          return createError({
            statusCode,
            statusMessage,
            fatal: false
          })
        }

        if (error.value.statusCode === 404) {
          this.registered = false
        } else {
          return createError({
            statusCode: 500,
            statusMessage: 'operation_not_found',
            fatal: false
          })
        }
      }

      if (data.value && data.value.store) {
        this.user = {
          id: data.value.id,
          name: data.value.name,
          profile_picture: data.value.profile_picture,
          email: data.value.email,
          language: data.value.language
        }
        this.registered = true
      }

      return {
        user: {
          id:this.user.id,
          name: this.user.name,
          profile_picture: this.user.profile_picture,
          email: this.user.email,
          language: this.user.language
        },
        registered: this.registered
      }
    },

    resetAuth: function () {
      this.authentication.expiration = null
      this.authentication.pin = null
    },

    refreshPin: async function (
        language: string,
        captcha?: string | null
    ) {
      if (!this.phone.iso) return createError({
        statusCode: 400,
        statusMessage: 'common.form.phone.error.invalid_phone',
        fatal: false
      })
      this.authentication.fallback = true
      const captchaObj = captcha ? { name: 'grecaptcha', token: captcha, visible: true } : undefined
      return this.login(this.phone.iso, language, captchaObj)
    },

    registerDummy: async function (
      locale: string,
      name: string,
      email: string | null
    ) {
      if(this.isSubmitting) return

      this.isSubmitting = true;

      const requestOptions = useRequestOptions()
      const checkout = useCheckoutStore();
      const event = checkout.$state.event

      const {data, error}: any = await useFetch(
        '/checkout/guestlist/account',
        {
          ...requestOptions,
          body: {
            event: event.id,
            name,
            mobile_phone: this.phone.iso,
            language_code: locale,
            email
          }
        }
      )

      if (error.value) {
        this.isSubmitting = false;

        return createError({
          statusCode: 400,
          statusMessage: 'common.form.phone.error.dummy_creation',
          fatal: false
        })
      }

      if (data.value) {
        return {
          user: data.value.id
        }
      }

      this.isSubmitting = false;
      return createError({
        statusCode: 400,
        statusMessage: 'common.form.phone.error.dummy_creation',
        fatal: false
      })
    },

    registerUser: async function (
      locale: string,
      form: RegisterAccountForm
    ) {

      if (!this.phone.iso || !this.authentication.pin) return createError({
        statusCode: 400,
        statusMessage: 'pin_invalid',
        fatal: false
      })

      const checkout = useCheckoutStore();
      const event = checkout.$state.event

      const requestOptions = useRequestOptions()

      const {data, error}: any = await useFetch(
        '/account/create',
        {
          ...requestOptions,
          body: {
            ...form,
            mobile_phone: this.phone.iso,
            pin: this.authentication.pin,
            fallback: this.authentication.fallback,
            event: event.id,
            timezone: event.schedule.timezone,
            language_code: locale
          }
        }
      )

      if (error.value) {
        if (
          error.value.data.error['invalid-params'] &&
          error.value.data.error['invalid-params'][0].field === 'pin'
        ) {
          return createError({
            statusCode: 400,
            statusMessage: 'pin_invalid',
            fatal: false
          })
        }
      }

      if (!data.value.id) return createError({statusCode: 400, statusMessage: 'common.form.phone.error.dummy_creation', fatal: false})

      this.user.id = data.value.id
      this.user.name = form.name ?? null
      this.registered = true
    },

    login: async function (
        phoneNumber: string,
        language: string,
        captcha?: { name: string; token: string | null; visible: boolean }
    ) {

      const requestOptions = useRequestOptions()

      if (language === 'pt') { language = 'pt_PT' }

      const {data, error}: any = await useFetch(
        '/account/phone_validation',
        {
          ...requestOptions,
          body: {
            mobile_phone: phoneNumber,
            fallback: this.authentication.fallback,
            language,
            captcha
          }
        }
      )

      if (error.value) {
        if (error.value.statusCode === 400) {
          if (error.value.data.error.code === 'operation_started') {
            return this.authentication.expiration ?? createError({
              statusCode: 400,
              statusMessage: 'common.form.phone.error.invalid_phone',
              fatal: false
            })
          }

          if (error.value.data.error.code === 'api.error.phone.too_many_retries') {
            return createError({statusMessage: 'api.error.phone.too_many_retries', statusCode: 400, fatal: false})
          }

          if (
            error.value.data.error['invalid-params'] &&
            error.value.data.error['invalid-params'][0].field === 'mobile_phone'
          ) {
            return createError({
              statusCode: 400,
              statusMessage: 'common.form.phone.error.invalid_phone',
              fatal: false
            })
          }
        }
      }

      if (!data.value.expires_at) {
        return createError({statusCode: 400, statusMessage: 'common.form.phone.error.invalid_phone', fatal: false})
      }

      this.authentication.expiration = new Date(data.value.expires_at)

      return this.authentication.expiration
    }
  }
})
